import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'
import BackLink from '../../../Components/BackLink'
import StarRating from '../../../Components/StarRating'
import { formatPrice, getStatusClasses, capitalize } from '../../../utils/formatters'

export default function Show({
  account,
  accounts,
  navigation,
  order,
  is_account_admin
}) {
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [refundReason, setRefundReason] = useState('')
  const [processing, setProcessing] = useState(false)

  const handleStatusChange = (newStatus) => {
    if (confirm(`Change order status to "${newStatus}"?`)) {
      router.patch(`/manage/orders/${order.id}`, {
        order: { status: newStatus }
      })
    }
  }

  const handleRefund = () => {
    if (!refundReason.trim()) return
    setProcessing(true)
    router.post(`/manage/orders/${order.id}/refund`, {
      refund: { reason: refundReason }
    }, {
      onFinish: () => setProcessing(false),
      onSuccess: () => {
        setShowRefundModal(false)
        setRefundReason('')
      }
    })
  }

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title={`Order #${order.order_number}`}
    >
      <BackLink href="/manage/orders">Back to Orders</BackLink>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Order Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              {order.order_code && (
                <h2 className="text-xl font-bold text-gray-900">
                  {order.order_code}
                </h2>
              )}
              <p className={`${order.order_code ? 'text-sm font-mono text-gray-500' : 'text-xl font-bold text-gray-900'}`}>
                Order #{order.order_number}
              </p>
              <p className="text-sm text-gray-500">
                {new Date(order.created_at).toLocaleString('en-GB')}
              </p>
            </div>
            <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getStatusClasses(order.status)}`}>
              {capitalize(order.status.replace('_', ' '))}
            </span>
          </div>
        </div>

        {/* Customer Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Customer</h3>
          <p className="text-sm text-gray-900">{order.email}</p>
          <p className="text-sm text-gray-900">{order.phone}</p>
          {order.pickup_time && (
            <p className="text-sm text-gray-600 mt-1">
              Pickup: {order.pickup_time}
            </p>
          )}
          {order.dining_option && (
            <p className="text-sm text-gray-600">
              {order.dining_option === 'sit_in' ? 'Dine In' : 'Takeaway'}
            </p>
          )}
        </div>

        {/* Customer Notes */}
        {order.customer_notes && (
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <h3 className="text-sm font-medium text-yellow-800 mb-1">Special Requests</h3>
            <p className="text-sm text-yellow-900 whitespace-pre-wrap">
              {order.customer_notes}
            </p>
          </div>
        )}

        {/* Customer Review */}
        {order.review && (
          <div className="px-6 py-4 border-b border-gray-200 bg-sky-50">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Customer Review</h3>
            <StarRating rating={order.review.rating} />
            {order.review.body && (
              <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                &ldquo;{order.review.body}&rdquo;
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {new Date(order.review.created_at).toLocaleString('en-GB')}
            </p>
          </div>
        )}

        {/* Refund Info */}
        {order.refunded_at && (
          <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
            <h3 className="text-sm font-medium text-red-800 mb-1">Refunded</h3>
            <p className="text-sm text-red-700">
              {new Date(order.refunded_at).toLocaleString('en-GB')} by {order.refunded_by}
            </p>
            <p className="text-sm text-red-700 mt-1">Reason: {order.refund_reason}</p>
          </div>
        )}

        {/* Order Items */}
        <div className="px-6 py-4">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Items</h3>
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th className="pb-2">Item</th>
                <th className="pb-2">Qty</th>
                <th className="pb-2 text-right">Price</th>
                <th className="pb-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.order_items.map((item) => (
                <tr key={item.id}>
                  <td className="py-2 text-sm text-gray-900">
                    {item.menu_item.name}
                    {item.menu_item.category && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({item.menu_item.category})
                      </span>
                    )}
                    {item.add_ons && item.add_ons.length > 0 && (
                      <span className="block text-xs text-gray-500">
                        {item.add_ons.map(a => `+ ${a.name}`).join(', ')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-600">
                    {item.quantity}
                  </td>
                  <td className="py-2 text-sm text-gray-600 text-right">
                    {formatPrice(item.unit_price_pence)}
                  </td>
                  <td className="py-2 text-sm text-gray-900 text-right font-medium">
                    {formatPrice(item.unit_price_pence * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <td colSpan="3" className="pt-3 text-sm font-medium text-gray-900 text-right">
                  Total
                </td>
                <td className="pt-3 text-lg font-bold text-gray-900 text-right">
                  {formatPrice(order.total_pence)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Update Status</h3>
          <div className="flex flex-wrap gap-2">
            {['confirmed', 'preparing', 'ready', 'collected', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                disabled={order.status === status}
                className={`px-3 py-1 text-sm rounded-md border transition ${
                  order.status === status
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                {capitalize(status.replace('_', ' '))}
              </button>
            ))}

            {is_account_admin && order.can_refund && (
              <button
                onClick={() => setShowRefundModal(true)}
                className="px-3 py-1 text-sm rounded-md border bg-red-50 text-red-700 hover:bg-red-100 border-red-300"
              >
                Refund Order
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Refund Order</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                This will issue a full refund of {formatPrice(order.total_pence)} to the customer.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Refund Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  rows={3}
                  placeholder="Reason for refund..."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowRefundModal(false)
                  setRefundReason('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRefund}
                disabled={processing || !refundReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Processing...' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
